/**
 * useAuth - REMEMBERING WHO IS LOGGED IN
 * --------------------------------------
 * React needs one shared place to answer "who is logged in?" so that the
 * navbar, the dashboard and every page agree. That shared place is a
 * "context", and this file creates it.
 *
 * Any component can then do:
 *
 *     const { user, login, logout } = useAuth();
 *
 * On first load we check for a saved token and ask the server who it belongs
 * to, which is what keeps you logged in after refreshing the page.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, paymentApi, tokenStore, SESSION_ENDED } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set when a login ended while the person was still sitting on the site,
  // and we know why. Today that means one thing: they were banned. The login
  // screen reads it to show the reason and the countdown - see LoginPage.jsx.
  //
  // Kept in memory and nowhere else, on purpose. It is news about a moment,
  // not a fact about the account, and a reload should not bring it back: the
  // account itself is what says whether somebody is banned, and it says so
  // again the moment they try to log in.
  const [endedSession, setEndedSession] = useState(null);

  // Is the site charging for membership at the moment? The backend decides
  // this (SUBSCRIPTIONS_ENABLED), and we ask once when the app starts so the
  // two can never disagree. While it is false the site is free and every
  // mention of paying is hidden.
  const [subscriptionsRequired, setSubscriptionsRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    paymentApi
      .config()
      .then((config) => {
        if (!cancelled) setSubscriptionsRequired(Boolean(config.subscriptionsRequired));
      })
      .catch(() => {
        // If we cannot reach the backend, assume free - never show a paywall
        // to somebody because of a failed request.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // On page load: if there is a saved token, find out who it belongs to.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!tokenStore.get()) {
        setLoading(false);
        return;
      }
      try {
        const { user: me } = await authApi.me();
        if (!cancelled) setUser(me);
      } catch {
        tokenStore.clear(); // token was expired or invalid
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * BEING SIGNED OUT BY SOMETHING OTHER THAN THE LOG OUT BUTTON.
   *
   * api.js shouts when the server says a login is over - expired, signed out
   * everywhere, or the account suspended. Until this listener existed, only
   * the saved token was thrown away and React went on believing somebody was
   * logged in: a banned person kept their name in the navbar and could browse
   * as though nothing had happened, right up until they tried to post a chore
   * and were told they needed to log in. That reads as a broken site, not as
   * a ban.
   *
   * Two different things happen here, and the difference matters.
   *
   * ALWAYS: empty the user, so every screen agrees with the token that has
   * already gone. That is the actual bug fix, and it is right for an ordinary
   * expiry as much as for a ban.
   *
   * ONLY FOR A BAN: send them to the login screen as well, carrying the
   * reason. An expired session on a public page should not throw somebody off
   * the page they were reading - ProtectedRoute already turns them away from
   * anywhere private, which is enough. A ban is different: they are owed the
   * reason, in the moderator's own words, and being quietly logged out with
   * no explanation is exactly the silence this is meant to end.
   */
  useEffect(() => {
    function handleSessionEnded(event) {
      const detail = event.detail || {};

      setUser(null);

      if (detail.ban) {
        setEndedSession(detail);
        // `replace` so the browser's back button does not walk them back onto
        // a page they can no longer load anything on.
        navigate('/login', { replace: true });
      }
    }

    window.addEventListener(SESSION_ENDED, handleSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED, handleSessionEnded);
  }, [navigate]);

  /**
   * Logs in - in one step for nearly everybody, in two for a moderator.
   *
   * A moderator gets no token here. The server sends a six-digit code to their
   * inbox and hands back a short-lived "challenge" instead, which is passed to
   * finishTwoFactorLogin below once they have typed the code in.
   *
   * The shape of what comes back says which happened, and the login screen
   * decides what to show:
   *
   *   { user }                      -> done, they are in
   *   { twoFactorRequired, ... }    -> show the code box
   *
   * Deliberately NOT stored anywhere: the challenge is held in the login
   * screen's own state and disappears if the page is reloaded. It is a
   * half-finished login, and a half-finished login has no business surviving
   * in localStorage next to a real one.
   */
  const login = useCallback(async (credentials) => {
    const result = await authApi.login(credentials);

    if (result.twoFactorRequired) {
      return {
        twoFactorRequired: true,
        challenge: result.challenge,
        sentTo: result.sentTo,
        expiresInMinutes: result.expiresInMinutes,
      };
    }

    tokenStore.set(result.token);
    setUser(result.user);
    // They are in, so whatever shut them out last time is over - a ban that
    // has run out heals the account on the way past (clearLapsedBan on the
    // server). Leaving the old notice lying about would put the ban screen
    // back over a login that had just succeeded.
    setEndedSession(null);
    return { user: result.user };
  }, []);

  /** The second half of a moderator login: the emailed code, or a recovery code. */
  const finishTwoFactorLogin = useCallback(async (challenge, code) => {
    const { token, user: me } = await authApi.loginTwoFactor(challenge, code);
    tokenStore.set(token);
    setUser(me);
    return me;
  }, []);

  const registerUser = useCallback(async (data) => {
    const { token, user: me } = await authApi.registerUser(data);
    tokenStore.set(token);
    setUser(me);
    return me;
  }, []);

  const registerStudent = useCallback(async (data) => {
    const { token, user: me } = await authApi.registerStudent(data);
    tokenStore.set(token);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setEndedSession(null);
  }, []);

  /**
   * Puts the ban notice away. The login screen calls this when the countdown
   * reaches zero, so the form comes back rather than leaving somebody staring
   * at an expired ban with nothing to do about it.
   */
  const clearEndedSession = useCallback(() => setEndedSession(null), []);

  /** Re-reads the account from the server, e.g. after paying or editing. */
  const refresh = useCallback(async () => {
    try {
      const { user: me } = await authApi.me();
      setUser(me);
      return me;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      finishTwoFactorLogin,
      logout,
      registerUser,
      registerStudent,
      refresh,
      setUser,
      endedSession,
      clearEndedSession,
      isLoggedIn: Boolean(user),
      isStudent: user?.role === 'student',
      isPoster: user?.role === 'user',
      subscriptionsRequired,
      // Students never need a membership. Posters only do once we start
      // charging - while the site is free, any poster can post.
      canPostChores:
        user?.role === 'user' &&
        (!subscriptionsRequired || user?.subscription?.status === 'active'),
    }),
    [
      user,
      loading,
      login,
      finishTwoFactorLogin,
      logout,
      registerUser,
      registerStudent,
      refresh,
      endedSession,
      clearEndedSession,
      subscriptionsRequired,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
